/**
 * src/TechnicianCloudHistory.js
 *
 * VISINVOKE Dashboard -> Technician — permanent Technician CLOUD work history.
 *
 * READ-ONLY Cloud history surface (Phase 7). This page NEVER executes anything on a Windows machine:
 * there are no Run / Apply / Registry / command controls here. Local Technician remains the only
 * execution environment; this is the durable, searchable record of work that was performed and
 * synchronised to VISINVOKE Cloud.
 *
 * Data comes from the PORTAL-authenticated, owner-scoped endpoints /v1/technician-history/* (cookie/JWT
 * auth). A user without an ACTIVE Technician Cloud entitlement sees a calm "not entitled" state — never
 * a broken page, and their LOCAL Technician access is unaffected (Cloud only governs history/sync).
 */
import React, { useState, useEffect, useCallback } from 'react';
import ProductNav from './components/ProductNav';
import API from './api';

const HISTORY_BASE = '/v1/technician-history';

// --- small presentational helpers ------------------------------------------

const C = {
  ink: '#1f2733', sub: '#5b6673', line: '#e4e8ee', card: '#ffffff', bg: '#f5f7fa',
  accent: '#2f6feb', good: '#1f9d55', warn: '#b7791f', bad: '#c53030', mute: '#8a94a1',
};

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}
function fmtDuration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return ms + ' ms';
  const s = Math.round(ms / 100) / 10;
  return s + ' s';
}
function toneFor(kind, value) {
  const v = String(value || '').toLowerCase();
  if (kind === 'status') {
    if (['completed', 'success'].some(x => v.includes(x))) return C.good;
    if (['fail', 'error', 'nonzero', 'rejected', 'declined'].some(x => v.includes(x))) return C.bad;
    if (['timedout', 'cancelled', 'pending'].some(x => v.includes(x))) return C.warn;
  }
  if (kind === 'verification') {
    if (v.includes('verified')) return C.good;
    if (v.includes('failed')) return C.bad;
    if (v.includes('reboot') || v.includes('manual') || v.includes('partial')) return C.warn;
  }
  return C.mute;
}
function Badge({ kind, value }) {
  if (value == null || value === '') return <span style={{ color: C.mute }}>—</span>;
  const tone = toneFor(kind, value);
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600,
      color: tone, background: tone + '18', border: '1px solid ' + tone + '33', whiteSpace: 'nowrap',
    }}>{String(value)}</span>
  );
}

function Pager({ page, pages, total, onPage }) {
  if (!pages || pages <= 1) {
    return <div style={{ color: C.sub, fontSize: 13 }}>{total != null ? total + ' record' + (total === 1 ? '' : 's') : ''}</div>;
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: C.sub }}>
      <button disabled={page <= 1} onClick={() => onPage(page - 1)} style={pagerBtn(page <= 1)}>Prev</button>
      <span>Page {page} of {pages}{total != null ? ' · ' + total + ' total' : ''}</span>
      <button disabled={page >= pages} onClick={() => onPage(page + 1)} style={pagerBtn(page >= pages)}>Next</button>
    </div>
  );
}
function pagerBtn(disabled) {
  return {
    padding: '5px 12px', borderRadius: 8, border: '1px solid ' + C.line, background: C.card,
    color: disabled ? C.mute : C.ink, cursor: disabled ? 'default' : 'pointer', fontWeight: 600,
  };
}

const TABS = [
  { key: 'sessions', label: 'Sessions' },
  { key: 'actions', label: 'Actions' },
  { key: 'workflows', label: 'Workflows' },
];

export default function TechnicianCloudHistory() {
  const [entitlement, setEntitlement] = useState(null); // null=loading; {entitled, cloud_status,...}
  const [entError, setEntError] = useState(null);
  const [tab, setTab] = useState('sessions');

  useEffect(() => {
    let alive = true;
    API.get(HISTORY_BASE + '/entitlement')
      .then(r => { if (alive) setEntitlement(r.data || {}); })
      .catch(err => {
        if (!alive) return;
        const code = err.response?.status;
        if (code === 403) setEntError('technician_access_required');
        else setEntError('unavailable');
      });
    return () => { alive = false; };
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <ProductNav />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 18px 60px' }}>
        <div style={{ marginBottom: 8 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: C.ink, margin: 0 }}>Technician Cloud — Work History</h1>
          <p style={{ color: C.sub, marginTop: 6, fontSize: 14 }}>
            The permanent, searchable record of work performed on client devices and synchronised to
            VISINVOKE Cloud. This is a history view only — repairs run in the local Technician app.
          </p>
        </div>

        {entError === 'technician_access_required' && (
          <Notice title="Technician access required"
                  body="This area is for VISINVOKE Technician accounts. Your local access is unaffected." />
        )}
        {entError === 'unavailable' && (
          <Notice title="History is temporarily unavailable"
                  body="We couldn't load Technician Cloud history right now. Please try again shortly." />
        )}

        {!entError && entitlement === null && <SkeletonBar />}

        {!entError && entitlement && entitlement.entitled !== true && (
          <Notice title="Technician Cloud is not active"
                  body={"Technician Cloud stores your permanent work history and syncs it here. Your local " +
                        "Technician tools keep working without it — Cloud only adds the permanent, searchable " +
                        "record shown on this page."} />
        )}

        {!entError && entitlement && entitlement.entitled === true && (
          <>
            <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid ' + C.line, marginTop: 14 }}>
              {TABS.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} style={{
                  padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer',
                  fontWeight: 700, fontSize: 14,
                  color: tab === t.key ? C.accent : C.sub,
                  borderBottom: '2px solid ' + (tab === t.key ? C.accent : 'transparent'),
                }}>{t.label}</button>
              ))}
            </div>
            <div style={{ marginTop: 16 }}>
              {tab === 'sessions' && <SessionsView />}
              {tab === 'actions' && <ActionsView />}
              {tab === 'workflows' && <WorkflowsView />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Notice({ title, body }) {
  return (
    <div style={{
      background: C.card, border: '1px solid ' + C.line, borderRadius: 12, padding: 24, marginTop: 14,
    }}>
      <div style={{ fontWeight: 800, color: C.ink, fontSize: 16 }}>{title}</div>
      <div style={{ color: C.sub, marginTop: 8, fontSize: 14, lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}
function SkeletonBar() {
  return <div style={{ height: 120, background: C.card, border: '1px solid ' + C.line, borderRadius: 12, marginTop: 14 }} />;
}

function Card({ children, pad = 0 }) {
  return <div style={{ background: C.card, border: '1px solid ' + C.line, borderRadius: 12, padding: pad, overflow: 'hidden' }}>{children}</div>;
}
function Th({ children, w }) {
  return <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: C.sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .3, width: w }}>{children}</th>;
}
function Td({ children, mute }) {
  return <td style={{ padding: '11px 12px', fontSize: 13.5, color: mute ? C.sub : C.ink, borderTop: '1px solid ' + C.line, verticalAlign: 'top' }}>{children}</td>;
}
function FilterBar({ children }) {
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, alignItems: 'center' }}>{children}</div>;
}
function TextInput({ value, onChange, placeholder, width }) {
  return <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    style={{ padding: '8px 10px', border: '1px solid ' + C.line, borderRadius: 8, fontSize: 13, width: width || 160, color: C.ink }} />;
}
function DateInput({ value, onChange }) {
  return <input type="date" value={value} onChange={e => onChange(e.target.value)}
    style={{ padding: '7px 10px', border: '1px solid ' + C.line, borderRadius: 8, fontSize: 13, color: C.ink }} />;
}

// generic list loader hook
function useHistoryList(path, filters, page) {
  const [data, setData] = useState({ items: [], total: 0, pages: 0, loading: true, error: null });
  const load = useCallback(() => {
    setData(d => ({ ...d, loading: true }));
    const params = { page, per_page: 25, ...cleanFilters(filters) };
    API.get(HISTORY_BASE + path, { params })
      .then(r => setData({
        items: r.data[collKey(path)] || [], total: r.data.total || 0, pages: r.data.pages || 0,
        loading: false, error: null,
      }))
      .catch(() => setData({ items: [], total: 0, pages: 0, loading: false, error: 'unavailable' }));
  }, [path, JSON.stringify(filters), page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return data;
}
function collKey(path) {
  if (path === '/sessions') return 'sessions';
  if (path === '/actions') return 'actions';
  return 'workflows';
}
function cleanFilters(f) {
  const out = {};
  Object.keys(f || {}).forEach(k => { if (f[k] !== '' && f[k] != null) out[k] = f[k]; });
  return out;
}

// ---- Sessions --------------------------------------------------------------
function SessionsView() {
  const [filters, setFilters] = useState({ q: '', device_id: '', date_from: '', date_to: '' });
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const set = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };
  const { items, total, pages, loading, error } = useHistoryList('/sessions', filters, page);

  return (
    <div>
      <FilterBar>
        <TextInput value={filters.q} onChange={v => set('q', v)} placeholder="Search title / summary" width={220} />
        <TextInput value={filters.device_id} onChange={v => set('device_id', v)} placeholder="Device ID" />
        <DateInput value={filters.date_from} onChange={v => set('date_from', v)} />
        <span style={{ color: C.sub }}>to</span>
        <DateInput value={filters.date_to} onChange={v => set('date_to', v)} />
      </FilterBar>
      <Card>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#fafbfc' }}>
            <Th>Started</Th><Th>Title</Th><Th>Device</Th><Th w={90}>Actions</Th><Th w={110}>State</Th><Th w={40}></Th>
          </tr></thead>
          <tbody>
            {loading && <tr><Td mute>Loading…</Td><Td /><Td /><Td /><Td /><Td /></tr>}
            {!loading && error && <tr><Td mute>Couldn’t load sessions.</Td><Td /><Td /><Td /><Td /><Td /></tr>}
            {!loading && !error && items.length === 0 && (
              <tr><Td mute>No sessions match these filters.</Td><Td /><Td /><Td /><Td /><Td /></tr>
            )}
            {!loading && items.map(s => (
              <React.Fragment key={s.workSessionId}>
                <tr onClick={() => setOpenId(openId === s.workSessionId ? null : s.workSessionId)}
                    style={{ cursor: 'pointer' }}>
                  <Td>{fmt(s.startedAt)}</Td>
                  <Td>{s.title || <span style={{ color: C.mute }}>Untitled session</span>}</Td>
                  <Td mute>{s.deviceId || '—'}</Td>
                  <Td>{s.actionCount != null ? s.actionCount : '—'}</Td>
                  <Td><Badge kind="status" value={s.state} /></Td>
                  <Td mute>{openId === s.workSessionId ? '▲' : '▼'}</Td>
                </tr>
                {openId === s.workSessionId && (
                  <tr><td colSpan={6} style={{ background: '#fafbfc', borderTop: '1px solid ' + C.line, padding: 0 }}>
                    <SessionDetail id={s.workSessionId} />
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </Card>
      <div style={{ marginTop: 12 }}><Pager page={page} pages={pages} total={total} onPage={setPage} /></div>
    </div>
  );
}

function SessionDetail({ id }) {
  const [d, setD] = useState({ loading: true });
  useEffect(() => {
    let alive = true;
    API.get(HISTORY_BASE + '/sessions/' + encodeURIComponent(id))
      .then(r => { if (alive) setD({ loading: false, ...r.data }); })
      .catch(() => { if (alive) setD({ loading: false, error: true }); });
    return () => { alive = false; };
  }, [id]);
  if (d.loading) return <div style={{ padding: 16, color: C.sub }}>Loading timeline…</div>;
  if (d.error) return <div style={{ padding: 16, color: C.sub }}>Couldn’t load this session.</div>;
  const timeline = buildTimeline(d);
  return (
    <div style={{ padding: '14px 16px' }}>
      {d.session && d.session.summary && <p style={{ marginTop: 0, color: C.ink }}>{d.session.summary}</p>}
      <SccLinkPanel workSessionId={id} initialBookingId={d.session && d.session.sccBookingId} />
      {timeline.length === 0 && <div style={{ color: C.sub }}>No recorded events for this session.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {timeline.map((ev, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
            <div style={{ width: 150, color: C.sub, fontSize: 12.5, flexShrink: 0 }}>{fmt(ev.at)}</div>
            <div style={{ width: 96, flexShrink: 0 }}><Badge kind="mute" value={ev.type} /></div>
            <div style={{ color: C.ink, fontSize: 13.5 }}>
              {ev.label}
              {ev.status ? <> · <Badge kind="status" value={ev.status} /></> : null}
              {ev.verification ? <> · <Badge kind="verification" value={ev.verification} /></> : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
function buildTimeline(d) {
  const ev = [];
  (d.actions || []).forEach(a => ev.push({
    at: a.occurredAt, type: a.intent || 'ACTION', label: a.actionId + (a.elevated ? '  (elevated)' : ''),
    status: a.status, verification: a.verificationState,
  }));
  (d.workflows || []).forEach(w => ev.push({
    at: w.startedAt, type: 'WORKFLOW', label: w.workflowSlug, status: w.status,
  }));
  (d.verifications || []).forEach(v => ev.push({
    at: v.occurredAt, type: 'VERIFY', label: v.method || 'verification', verification: v.verificationState,
  }));
  (d.rollbacks || []).forEach(r => ev.push({
    at: r.occurredAt, type: 'ROLLBACK', label: r.method || 'rollback', status: r.status,
  }));
  ev.sort((a, b) => new Date(a.at || 0) - new Date(b.at || 0));
  return ev;
}

// ---- SCC job link (association only; NEVER an execution surface) ------------
// Lets the technician attach THIS work session to one of the SCC jobs they are the assignee/creator of.
// Uses the cookie-authed portal endpoints, which delegate to the dual-domain link service (the backend
// re-checks authorization on every write). Linking associates history; it runs nothing. An unrelated job
// can never be found or linked here — the backend scopes the search and returns an opaque failure.
function SccLinkPanel({ workSessionId, initialBookingId }) {
  const [links, setLinks] = useState(null);      // null=loading
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);  // null=not searched yet
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    API.get(HISTORY_BASE + '/scc/links', { params: { work_session_id: workSessionId } })
      .then(r => setLinks(((r.data && r.data.links) || []).filter(l => l.status === 'active')))
      .catch(() => setLinks([]));
  }, [workSessionId]);
  useEffect(() => { load(); }, [load]);

  const runSearch = () => {
    setResults('loading'); setMsg(null);
    API.get(HISTORY_BASE + '/scc/bookings', { params: { q } })
      .then(r => setResults((r.data && r.data.bookings) || []))
      .catch(() => setResults([]));
  };
  const doLink = (b) => {
    setBusy(true); setMsg(null);
    API.post(HISTORY_BASE + '/scc/link', { work_session_id: workSessionId, booking_id: b.bookingId })
      .then(() => { setSearching(false); setResults(null); setQ(''); load(); })
      .catch(err => setMsg(err.response?.data?.error === 'booking_not_linkable'
        ? 'That job is not one you can link.' : 'Could not link this job.'))
      .finally(() => setBusy(false));
  };
  const doUnlink = (l) => {
    setBusy(true); setMsg(null);
    API.post(HISTORY_BASE + '/scc/unlink', { work_session_id: workSessionId, booking_id: l.sccBookingId })
      .then(() => load()).catch(() => setMsg('Could not unlink.')).finally(() => setBusy(false));
  };

  const box = { border: '1px solid ' + C.line, borderRadius: 10, padding: '12px 14px', marginBottom: 14, background: C.card };
  const btn = (primary) => ({
    padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: busy ? 'default' : 'pointer',
    border: '1px solid ' + (primary ? C.accent : C.line), background: primary ? C.accent : C.card,
    color: primary ? '#fff' : C.ink, opacity: busy ? 0.6 : 1,
  });

  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ fontWeight: 800, color: C.ink, fontSize: 13.5 }}>SCC job</div>
        {!searching && (
          <button onClick={() => { setSearching(true); setResults(null); }} style={btn(false)}>
            {links && links.length ? 'Link another job' : 'Link to an SCC job'}
          </button>
        )}
      </div>

      {links === null && <div style={{ color: C.sub, fontSize: 13, marginTop: 8 }}>Loading link…</div>}

      {links && links.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {links.map(l => (
            <div key={l.linkId} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5 }}>
              <Badge kind="mute" value={l.sccBookingReference || l.sccBookingId} />
              <span style={{ color: C.ink, flex: 1 }}>{l.sccBookingSummary || '—'}</span>
              <span style={{ color: C.mute, fontSize: 12 }}>{l.linkBasis}</span>
              <button onClick={() => doUnlink(l)} disabled={busy} style={btn(false)}>Unlink</button>
            </div>
          ))}
        </div>
      )}

      {links && links.length === 0 && !searching && (
        <div style={{ color: C.sub, fontSize: 13, marginTop: 8 }}>
          Not linked to an SCC job.{initialBookingId ? ' (pending sync)' : ''}
        </div>
      )}

      {searching && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search your SCC jobs (reference, device, fault)"
              onKeyDown={e => { if (e.key === 'Enter') runSearch(); }}
              style={{ padding: '8px 10px', border: '1px solid ' + C.line, borderRadius: 8, fontSize: 13, width: 320, color: C.ink }} />
            <button onClick={runSearch} disabled={busy} style={btn(true)}>Search</button>
            <button onClick={() => { setSearching(false); setResults(null); setMsg(null); }} style={btn(false)}>Cancel</button>
          </div>
          {results === 'loading' && <div style={{ color: C.sub, fontSize: 13, marginTop: 8 }}>Searching…</div>}
          {Array.isArray(results) && results.length === 0 && (
            <div style={{ color: C.sub, fontSize: 13, marginTop: 8 }}>No matching jobs assigned to you.</div>
          )}
          {Array.isArray(results) && results.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {results.map(b => (
                <div key={b.bookingId} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5,
                  border: '1px solid ' + C.line, borderRadius: 8, padding: '8px 10px' }}>
                  <Badge kind="mute" value={b.reference} />
                  <span style={{ color: C.ink, flex: 1 }}>
                    {[b.customerName, b.deviceSummary, b.faultSummary || b.serviceType].filter(Boolean).join(' · ') || '—'}
                  </span>
                  <span style={{ color: C.mute, fontSize: 12 }}>{b.relationship}</span>
                  <button onClick={() => doLink(b)} disabled={busy} style={btn(true)}>Link</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {msg && <div style={{ color: C.bad, fontSize: 12.5, marginTop: 8 }}>{msg}</div>}
    </div>
  );
}

// ---- Actions ---------------------------------------------------------------
function ActionsView() {
  const [filters, setFilters] = useState({ q: '', status: '', verification_state: '', device_id: '', date_from: '', date_to: '' });
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const set = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };
  const { items, total, pages, loading, error } = useHistoryList('/actions', filters, page);
  return (
    <div>
      <FilterBar>
        <TextInput value={filters.q} onChange={v => set('q', v)} placeholder="Search action ID" width={200} />
        <TextInput value={filters.status} onChange={v => set('status', v)} placeholder="Status" width={130} />
        <TextInput value={filters.verification_state} onChange={v => set('verification_state', v)} placeholder="Verification" width={140} />
        <DateInput value={filters.date_from} onChange={v => set('date_from', v)} />
        <span style={{ color: C.sub }}>to</span>
        <DateInput value={filters.date_to} onChange={v => set('date_to', v)} />
      </FilterBar>
      <Card>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#fafbfc' }}>
            <Th>When</Th><Th>Action</Th><Th w={80}>Intent</Th><Th w={120}>Result</Th><Th w={120}>Verified</Th><Th w={40}></Th>
          </tr></thead>
          <tbody>
            {loading && <tr><Td mute>Loading…</Td><Td /><Td /><Td /><Td /><Td /></tr>}
            {!loading && error && <tr><Td mute>Couldn’t load actions.</Td><Td /><Td /><Td /><Td /><Td /></tr>}
            {!loading && !error && items.length === 0 && (
              <tr><Td mute>No actions match these filters.</Td><Td /><Td /><Td /><Td /><Td /></tr>
            )}
            {!loading && items.map(a => (
              <React.Fragment key={a.actionEventId}>
                <tr onClick={() => setOpenId(openId === a.actionEventId ? null : a.actionEventId)} style={{ cursor: 'pointer' }}>
                  <Td>{fmt(a.occurredAt)}</Td>
                  <Td>{a.actionId}{a.elevated ? <span style={{ color: C.warn, fontSize: 12 }}> · elevated</span> : null}</Td>
                  <Td mute>{a.intent}</Td>
                  <Td><Badge kind="status" value={a.status} /></Td>
                  <Td><Badge kind="verification" value={a.verificationState} /></Td>
                  <Td mute>{openId === a.actionEventId ? '▲' : '▼'}</Td>
                </tr>
                {openId === a.actionEventId && (
                  <tr><td colSpan={6} style={{ background: '#fafbfc', borderTop: '1px solid ' + C.line, padding: 0 }}>
                    <ActionDetail id={a.actionEventId} row={a} />
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </Card>
      <div style={{ marginTop: 12 }}><Pager page={page} pages={pages} total={total} onPage={setPage} /></div>
    </div>
  );
}

function ActionDetail({ id, row }) {
  const [d, setD] = useState({ loading: true });
  useEffect(() => {
    let alive = true;
    API.get(HISTORY_BASE + '/actions/' + encodeURIComponent(id))
      .then(r => { if (alive) setD({ loading: false, ...r.data }); })
      .catch(() => { if (alive) setD({ loading: false, error: true }); });
    return () => { alive = false; };
  }, [id]);
  const a = (d.action) || row || {};
  const kv = [
    ['Action ID', a.actionId],
    ['Intent', a.intent],
    ['Elevated', a.elevated ? 'Yes' : 'No'],
    ['Result', a.status],
    ['Verification', a.verificationState],
    ['Rollback available', a.rollbackAvailability],
    ['Exit code', a.exitCode == null ? '—' : String(a.exitCode)],
    ['Duration', fmtDuration(a.durationMs)],
    ['Device', a.deviceId],
    ['Catalogue version', a.catalogueVersion],
    ['Operation fingerprint', a.operationFingerprint],
    ['When', fmt(a.occurredAt)],
  ];
  return (
    <div style={{ padding: '14px 16px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '8px 24px' }}>
        {kv.map(([k, v]) => (
          <div key={k} style={{ fontSize: 13 }}>
            <div style={{ color: C.mute, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: .3 }}>{k}</div>
            <div style={{ color: C.ink, wordBreak: 'break-all' }}>{v == null || v === '' ? '—' : String(v)}</div>
          </div>
        ))}
      </div>
      {d.loading && <div style={{ color: C.sub, marginTop: 10 }}>Loading related events…</div>}
      {!d.loading && ((d.verifications || []).length > 0 || (d.rollbacks || []).length > 0) && (
        <div style={{ marginTop: 14 }}>
          {(d.verifications || []).map((v, i) => (
            <div key={'v' + i} style={{ fontSize: 13, color: C.ink, marginTop: 4 }}>
              Verification · <Badge kind="verification" value={v.verificationState} /> {v.method ? '· ' + v.method : ''} · {fmt(v.occurredAt)}
            </div>
          ))}
          {(d.rollbacks || []).map((r, i) => (
            <div key={'r' + i} style={{ fontSize: 13, color: C.ink, marginTop: 4 }}>
              Rollback · <Badge kind="status" value={r.status} /> {r.method ? '· ' + r.method : ''} · {fmt(r.occurredAt)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Workflows -------------------------------------------------------------
function WorkflowsView() {
  const [filters, setFilters] = useState({ status: '', workflow_slug: '' });
  const [page, setPage] = useState(1);
  const set = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };
  const { items, total, pages, loading, error } = useHistoryList('/workflows', filters, page);
  return (
    <div>
      <FilterBar>
        <TextInput value={filters.workflow_slug} onChange={v => set('workflow_slug', v)} placeholder="Workflow" width={200} />
        <TextInput value={filters.status} onChange={v => set('status', v)} placeholder="Status" width={130} />
      </FilterBar>
      <Card>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#fafbfc' }}>
            <Th>Started</Th><Th>Workflow</Th><Th w={120}>Result</Th><Th w={110}>Steps</Th><Th>Finished</Th>
          </tr></thead>
          <tbody>
            {loading && <tr><Td mute>Loading…</Td><Td /><Td /><Td /><Td /></tr>}
            {!loading && error && <tr><Td mute>Couldn’t load workflows.</Td><Td /><Td /><Td /><Td /></tr>}
            {!loading && !error && items.length === 0 && (
              <tr><Td mute>No workflow runs recorded.</Td><Td /><Td /><Td /><Td /></tr>
            )}
            {!loading && items.map(w => (
              <tr key={w.workflowRunId}>
                <Td>{fmt(w.startedAt)}</Td>
                <Td>{w.workflowSlug}</Td>
                <Td><Badge kind="status" value={w.status} /></Td>
                <Td mute>{w.stepCompleted != null && w.stepTotal != null ? w.stepCompleted + ' / ' + w.stepTotal : '—'}</Td>
                <Td mute>{fmt(w.finishedAt)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <div style={{ marginTop: 12 }}><Pager page={page} pages={pages} total={total} onPage={setPage} /></div>
    </div>
  );
}
